import { useApolloClient } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { GqlJob, GqlMapEditorLayer } from "~/__generated__";
import WorkerJobStatus from "~/modules/app/components/WorkerJobStatus";
import { GqlProjectMetadata } from "~/modules/project/api/useMeGetProjectInfoQuery";

import FileUploaderStatus from "../../../media/components/FileUploaderStatus";
import { MapEditorGetAllLayerFeaturesDocument } from "../../api/useMapEditorGetAllLayerFeatures.generated";
import useMapEditorImportStart from "../../api/useMapEditorImportStart";
import MapEditorLayerAddressImportFeedback from "../MapEditorLayerAddressImportFeedback";

const feedback = (data: GqlJob) => <MapEditorLayerAddressImportFeedback job={data} />;

type Props = {
    project: GqlProjectMetadata;
    layer: GqlMapEditorLayer;
};

export default function MapEditorLayerAddressImport({ layer, project }: Props) {
    const { t } = useTranslation("mapEditor");
    const client = useApolloClient();

    const { startAddressImportJob } = useMapEditorImportStart();

    const handleImportFinished = useCallback(async () => {
        await client.refetchQueries({
            include: [MapEditorGetAllLayerFeaturesDocument],
        });
    }, [client]);

    async function startImportJob(mediaId: string) {
        startAddressImportJob({
            projectId: project.id,
            layerId: layer.id,
            mediaId,
        });
    }

    return (
        <Stack padding={2} rowGap={2}>
            <Typography variant="h5">{t("mapEditorAddressImport.title")}</Typography>
            <FileUploaderStatus
                onUploadFinished={([e]) => startImportJob(e.id)}
                multiple={false}
                fileTypesToAccept={{ "text/csv": [".csv"] }}
            />

            {layer.addressImportJobId && (
                <WorkerJobStatus
                    worker="AddressImportWorker"
                    jobId={layer.addressImportJobId}
                    onFinished={handleImportFinished}
                    renderFeedback={feedback}
                />
            )}
        </Stack>
    );
}
